/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useState } from 'react';
import { scroller, Link as ScrollLink } from 'react-scroll';
import parse from 'parse-json';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import { themeStyles, defaultStyles } from './Link.styles';

const defaultReactScrollOptions = {
    spy: true,
    smooth: true,
    offset: -50,
    duration: 300,
    isDynamic: true,
};

interface QueryParameter {
    queryParameter: string;
    queryValue: string;
}
export interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    navigateWithinPage: boolean;
    link: string;
    linkName: string;
    showIcon?: boolean;
    iconName?: string;
    iconPosition?: string;
    openInNewTab?: boolean;
    target?: string;
    queryParameters?: QueryParameter[];
    isWithinIframe: boolean;
    linkClassName: string;
    linkHref: string;
    typographyClass?: string;
    scrollOptions?: string;
}

const Link = (props: commonTypes.WidgetInstanceProps) => {
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const {
        styles,
        link,
        linkName,
        showIcon,
        iconName,
        iconPosition,
        target,
        openInNewTab,
        queryParameters,
        scrollOptions,
        ...rest
    } = widgetProperties || props;
    const { data } = props;

    const i18n = commonHooks.useLocalization();

    const linkNameHtml = i18n.format(linkName, data);
    const linkHtml = i18n.format(link, data);
    const theme = data?.themeSelected;

    const [hrefState, setHrefState] = useState('');
    const [queryState, setQueryState] = useState('');

    useEffect(() => {
        queryParameters?.forEach((query) => {
            const queryParameter = i18n.format(query.queryParameter, data);
            const queryValue = i18n.format(query.queryValue, data);

            setQueryState((prev) =>
                queryParameters.length === 1
                    ? prev.concat(`?${queryParameter}=${queryValue}`)
                    : prev.concat(`&${queryParameter}=${queryValue}`)
            );
        });

        queryParameters && queryParameters.length > 0
            ? setHrefState(`${linkHtml}${queryState}`)
            : setHrefState(linkHtml);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryParameters, data, linkHtml]);

    let linkTarget = target || '_self';

    if (openInNewTab) {
        linkTarget = '_blank';
    }

    // Options for React-scroll
    let options: any = defaultReactScrollOptions;

    try {
        options = { ...options, ...parse((scrollOptions as string) || '{}') };
    } catch (error) {
        options = { ...options };
    }

    const handleNavKeyPress = (event: any) => {
        if (event.key === 'Enter' || event.keyCode === 13) {
            // Use react-scroll to smoothly scroll to the anchor element
            scroller.scrollTo(rest.linkHref, { ...options });
        }
    };

    return rest.navigateWithinPage ? (
        <ScrollLink
            css={css`
                ${!theme ? defaultStyles : themeStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
            className={`${rest.isWithinIframe ? rest.linkClassName : ''} ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
            onKeyPress={(e) => handleNavKeyPress(e)}
            key={rest.linkHref}
            data-key={rest.linkHref}
            to={rest.linkHref}
            tabIndex={-1}
        >
            <button aria-label={linkNameHtml}>
                {showIcon && (
                    <span
                        className={`icon ${iconPosition === 'Right' ? 'icon-right' : 'icon-left'}`}
                    >
                        <i className={iconName} />
                    </span>
                )}
                <span className="link-name">{linkNameHtml}</span>
            </button>
        </ScrollLink>
    ) : (
        <a
            css={css`
                ${!theme ? defaultStyles : themeStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
            className={`${rest.isWithinIframe ? rest.linkClassName : ''} ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
            href={hrefState}
            data-key={rest.linkHref}
            id="link"
            rel="noreferrer"
            target={linkTarget}
            tabIndex={-1}
        >
            <button aria-label={linkNameHtml}>
                {showIcon && (
                    <span
                        className={`icon ${iconPosition === 'Right' ? 'icon-right' : 'icon-left'}`}
                    >
                        <i className={iconName} />
                    </span>
                )}
                <span className="link-name">{linkNameHtml}</span>
            </button>
        </a>
    );
};

export default Link;
